import {
  Outlet,
  createFileRoute,
  redirect,
} from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { createClient } from '@kairos/supabase/browser'

import { AdminShell } from '~/components/admin-shell/AdminShell'

/**
 * Pathless layout segment for everything under `/admin`. Renders the rail +
 * navbar + detail-panel chrome from `01-shell.md`.
 *
 * `beforeLoad` enforces auth: signed-out users get bounced to the
 * authentication shell. Platform-admin gating happens further inside —
 * the auth resolution is a query against the API Worker, not a synchronous
 * value, so we render the shell while it loads and replace the canvas with
 * a permission-denied empty state if the check fails.
 */
export const Route = createFileRoute('/admin')({
  beforeLoad: async ({ context, location }) => {
    // Not signed in — bounce to login with a returnTo so they land back here.
    if (!context.auth.loading && !context.auth.user) {
      throw redirect({
        to: '/authentication/login',
        search: { returnTo: location.pathname },
      })
    }
    // Signed in but confirmed non-admin — redirect to login with no returnTo.
    // The RouterBridge effect also signs them out; this prevents the shell
    // from rendering at all while that async sign-out is in flight.
    if (context.auth.isPlatformAdmin === false) {
      throw redirect({ to: '/authentication/login' })
    }
    // CASA 3.3.1: a platform admin must be on aal2. Send them to the TOTP challenge so
    // they can satisfy it (or enrol), rather than showing a shell whose every request 403s.
    if (context.auth.user) {
      const supabase = createClient()
      const { data } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
      if (data?.currentLevel !== 'aal2') {
        throw redirect({ to: '/authentication/mfa' })
      }
    }
  },
  component: AdminLayout,
})

function AdminLayout(): ReactNode {
  return (
    <AdminShell>
      <Outlet />
    </AdminShell>
  )
}
